import { useMemo } from "react";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  ArrowRight,
  Clock,
  LineChart,
  Scale,
  ShoppingBag,
  Sparkles,
  TrendingDown,
  TrendingUp,
  UserMinus,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";

const PERIOD_OPTIONS = [
  { days: 1, label: "1 J" },
  { days: 7, label: "7 J" },
  { days: 30, label: "30 J" },
  { days: 90, label: "3 M" },
  { days: 180, label: "6 M" },
  { days: 365, label: "1 A" },
];

function eurFmt(v, decimals = 0) {
  return Number(v ?? 0).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: decimals,
  });
}

function nFmt(v) {
  return Number(v ?? 0).toLocaleString("fr-FR");
}

function pctFmt(v) {
  const n = Number(v ?? 0);
  return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`;
}

function formatChartDay(isoDate, days) {
  const d = new Date(`${isoDate}T12:00:00`);
  if (days <= 1) {
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  }
  if (days <= 31) {
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  }
  if (days <= 180) {
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  }
  return d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
}

function periodLabelFr(days) {
  const opt = PERIOD_OPTIONS.find((p) => p.days === days);
  if (opt) return opt.label;
  if (days === 365) return "1 A";
  if (days === 180) return "6 M";
  if (days === 90) return "3 M";
  return `${days} J`;
}

function chartXAxisInterval(days) {
  if (days <= 7) return 0;
  if (days <= 31) return "preserveStartEnd";
  if (days <= 90) return Math.max(1, Math.floor(days / 12));
  if (days <= 180) return Math.max(1, Math.floor(days / 10));
  return Math.max(1, Math.floor(days / 14));
}

function formatActivityWhen(iso) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  const diffH = (Date.now() - t) / 3600000;
  if (diffH < 1) return "À l'instant";
  if (diffH < 24) return `Il y a ${Math.max(1, Math.round(diffH))} h`;
  const diffD = diffH / 24;
  if (diffD < 7) return `Il y a ${Math.max(1, Math.round(diffD))} j`;
  return new Date(t).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

const ACTIVITY_ICON = {
  sale: ShoppingBag,
  user: UserPlus,
  dispute: AlertTriangle,
  listing: Sparkles,
};

const ACTIVITY_TONE = {
  sale: "bg-emerald-100 text-emerald-800",
  user: "bg-sky-100 text-sky-800",
  dispute: "bg-amber-100 text-amber-900",
  listing: "bg-violet-100 text-violet-800",
};

function ClickableKpiCard({ label, value, hint, Icon, onClick, testId, subValue, variant = "default" }) {
  const El = onClick ? "button" : "div";
  return (
    <El
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "rounded-xl border p-5 shadow-sm text-left transition-all w-full",
        onClick && "cursor-pointer hover:shadow-md hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7FF9]/40",
        variant === "positive" && "border-emerald-200/80 bg-emerald-50/30",
        variant === "negative" && "border-red-200/80 bg-red-50/30",
        variant === "default" && "border-slate-200/90 bg-white",
      )}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
          <p className="mt-2 font-display text-2xl font-black tabular-nums tracking-tight text-[#0B0D12] sm:text-3xl">
            {value}
          </p>
          {subValue ? <p className="mt-0.5 text-sm font-semibold tabular-nums text-slate-600">{subValue}</p> : null}
          {hint ? <p className="mt-1.5 text-xs leading-snug text-slate-600">{hint}</p> : null}
          {onClick ? (
            <p className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#2D7FF9]">
              Voir le détail
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </p>
          ) : null}
        </div>
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
            variant === "positive" && "bg-emerald-100 text-emerald-800",
            variant === "negative" && "bg-red-100 text-red-800",
            variant === "default" && "bg-slate-100 text-slate-600",
          )}
        >
          <Icon className="h-5 w-5" aria-hidden strokeWidth={2} />
        </div>
      </div>
    </El>
  );
}

function UrgencyCard({ label, count, hint, Icon, onClick, testId, variant = "amber" }) {
  if (!count || count <= 0) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left shadow-sm transition-all",
        "hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7FF9]/40",
        variant === "red"
          ? "border-red-200 bg-red-50/90 hover:border-red-300"
          : "border-amber-200 bg-amber-50/90 hover:border-amber-300",
      )}
      data-testid={testId}
    >
      <div
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
          variant === "red" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-900",
        )}
      >
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-[#0B0D12]">
          {label}
          <span className="ml-1.5 tabular-nums text-[#2D7FF9]">({count})</span>
        </p>
        {hint ? <p className="mt-0.5 text-xs text-slate-600">{hint}</p> : null}
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
    </button>
  );
}

function SnapshotTile({ label, value, hint, onClick, testId }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "rounded-lg border border-slate-200/80 bg-white px-3 py-3 text-center transition-all",
        onClick &&
          "cursor-pointer hover:border-[#2D7FF9]/30 hover:bg-sky-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7FF9]/30 w-full",
      )}
      data-testid={testId}
    >
      <p className="text-[10px] font-semibold uppercase text-slate-500">{label}</p>
      <p className="mt-0.5 text-xl font-bold tabular-nums text-[#0B0D12]">{value}</p>
      {hint ? <p className="mt-0.5 text-[10px] text-slate-500">{hint}</p> : null}
    </Tag>
  );
}

function SaasMiniKpi({ label, value, hint, Icon, testId, tone = "sky" }) {
  return (
    <div
      className="flex min-w-0 gap-3 rounded-xl border border-slate-200/80 bg-white px-3 py-3"
      data-testid={testId}
    >
      <div
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
          tone === "emerald" && "bg-emerald-100 text-emerald-800",
          tone === "red" && "bg-red-100 text-red-800",
          tone === "sky" && "bg-[#2D7FF9]/10 text-[#2D7FF9]",
        )}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </div>
      <div className="min-w-0">
        <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <p className="mt-0.5 font-display text-lg font-black tabular-nums text-[#0B0D12]">{value}</p>
        {hint ? <p className="text-[10px] text-slate-500">{hint}</p> : null}
      </div>
    </div>
  );
}

export default function AdminOverviewDashboard({
  stats,
  overviewDays,
  onOverviewDaysChange,
  loading = false,
  snapshot = {},
  onNavigateTab,
  onOpenTransactions,
  onOpenDispute,
  onOpenUser,
  onOpenDossier,
}) {
  const period = stats?.period && typeof stats.period === "object" ? stats.period : null;
  const subPeriod =
    stats?.subscription_period && typeof stats.subscription_period === "object"
      ? stats.subscription_period
      : null;
  const days = period?.days ?? overviewDays ?? 30;
  const periodLabel = periodLabelFr(days);

  const gmvChartData = useMemo(() => {
    const series = Array.isArray(period?.series) ? period.series : [];
    return series.map((row) => ({
      ...row,
      label: formatChartDay(row.date, days),
      commissions: Number(row.commissions_eur ?? 0),
      gmv: Number(row.gmv_eur ?? row.revenue_eur ?? 0),
    }));
  }, [period?.series, days]);

  const subsChartData = useMemo(() => {
    const series = Array.isArray(subPeriod?.series) ? subPeriod.series : [];
    return series.map((row) => ({
      ...row,
      label: formatChartDay(row.date, days),
      active: Number(row.subscribers_active ?? 0),
      newSubs: Number(row.subscribers_new ?? 0),
      churned: Number(row.subscribers_churned ?? 0),
    }));
  }, [subPeriod?.series, days]);

  const gmvChartMax = useMemo(() => {
    let m = 1;
    for (const row of gmvChartData) {
      m = Math.max(m, row.commissions, row.gmv);
    }
    return m;
  }, [gmvChartData]);

  const subsChartMax = useMemo(() => {
    let m = 1;
    for (const row of subsChartData) {
      m = Math.max(m, row.active, row.newSubs, row.churned);
    }
    return m;
  }, [subsChartData]);

  const gmvChartEmpty = gmvChartData.length === 0;
  const gmvChartNoRevenue = gmvChartData.length > 0 && gmvChartMax <= 0;
  const subsChartEmpty = subsChartData.length === 0;

  const marketplaceKpis = useMemo(() => {
    if (!period) return null;
    const goTx = () => onOpenTransactions?.({ filter: "all" });
    return [
      {
        key: "commissions",
        label: "Commissions plateforme",
        value: eurFmt(period.commissions_eur ?? 0),
        hint: `10 % HT vendeur · ${periodLabel}`,
        Icon: TrendingUp,
        onClick: goTx,
        testId: "admin-dash-commissions",
      },
      {
        key: "gmv",
        label: "Volume ventes (GMV)",
        value: eurFmt(period.gmv_eur ?? period.revenue_eur ?? 0),
        hint: `Hors abonnements · ${periodLabel}`,
        Icon: LineChart,
        onClick: goTx,
        testId: "admin-dash-gmv",
      },
      {
        key: "sales",
        label: "Ventes confirmées",
        value: nFmt(period.sales_count),
        hint: "RDV validés · fonds débloqués",
        Icon: ShoppingBag,
        onClick: goTx,
        testId: "admin-dash-sales",
      },
      {
        key: "users",
        label: "Nouveaux comptes",
        value: nFmt(period.new_users),
        hint: `Inscriptions · ${periodLabel}`,
        Icon: UserPlus,
        onClick: () => onNavigateTab?.("users"),
        testId: "admin-dash-users",
      },
      {
        key: "disputes",
        label: "Litiges ouverts (période)",
        value: nFmt(period.new_disputes),
        subValue:
          Number(snapshot.disputesOpen ?? stats?.disputes_open ?? 0) > 0
            ? `${nFmt(snapshot.disputesOpen ?? stats?.disputes_open)} en cours au total`
            : null,
        hint: "Cliquez pour traiter",
        Icon: AlertTriangle,
        onClick: () => onNavigateTab?.("disputes"),
        testId: "admin-dash-disputes",
      },
    ];
  }, [period, periodLabel, stats?.disputes_open, snapshot.disputesOpen, onNavigateTab, onOpenTransactions]);

  const activeSubs = Number(stats?.subscribers_active_count || 0);
  const mrr = Number(stats?.mrr_eur || 0);
  const arpu = activeSubs > 0 ? mrr / activeSubs : 0;
  const subsNew = Number(subPeriod?.subscribers_new ?? 0);
  const subsChurned = Number(subPeriod?.subscribers_churned ?? 0);
  const subsNet = Number(subPeriod?.subscribers_net ?? subsNew - subsChurned);
  const churnRate = Number(subPeriod?.churn_rate_pct ?? 0);
  const subsStart = Number(subPeriod?.subscribers_active_start ?? 0);

  const activity = Array.isArray(period?.recent_activity) ? period.recent_activity : [];

  const handleActivityClick = (a) => {
    const id = a?.entity_id;
    if (!id) return;
    switch (a.type) {
      case "sale":
        onOpenTransactions?.({ filter: "all", detailId: String(id) });
        break;
      case "user":
        onOpenUser?.(String(id));
        break;
      case "dispute":
        onOpenDispute?.(String(id));
        break;
      case "listing":
        onOpenDossier?.(String(id));
        break;
      default:
        break;
    }
  };

  const urgencies = [
    {
      key: "disputes",
      label: "Litiges à traiter",
      count: snapshot.disputesOpen ?? stats?.disputes_open ?? 0,
      hint: "Ouverts ou en revue — priorité métier",
      Icon: Scale,
      onClick: () => onNavigateTab?.("disputes"),
      testId: "admin-dash-urgency-disputes",
    },
    {
      key: "tx",
      label: "Transactions à surveiller",
      count: snapshot.txAttention ?? 0,
      hint: "Litige, versement dû, chargeback ou échec Stripe",
      Icon: Wallet,
      onClick: () => onOpenTransactions?.({ filter: "attention" }),
      testId: "admin-dash-urgency-tx",
    },
    {
      key: "payout",
      label: "Versements vendeur dus",
      count: snapshot.payoutDue ?? 0,
      hint: "Délai 72 h écoulé — à déclencher si conforme",
      Icon: Clock,
      onClick: () => onOpenTransactions?.({ filter: "attention" }),
      testId: "admin-dash-urgency-payout",
      variant: snapshot.payoutDue > 0 ? "red" : "amber",
    },
    {
      key: "decision",
      label: "Décisions RDV en attente",
      count: snapshot.pendingRdv ?? 0,
      hint: "Appel effectué — l’acheteur n’a pas encore tranché",
      Icon: AlertTriangle,
      onClick: () => onOpenTransactions?.({ filter: "all" }),
      testId: "admin-dash-urgency-rdv",
    },
  ].filter((u) => u.count > 0);

  return (
    <div className="space-y-6" data-testid="admin-overview-dashboard">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight text-[#0B0D12] sm:text-2xl">
            Pilotage plateforme
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Abonnements SaaS, marketplace et actions urgentes — la période s’applique aux courbes et aux flux.
          </p>
        </div>
        <div
          className="inline-flex max-w-full flex-wrap justify-end rounded-lg border border-slate-200 bg-slate-50/80 p-0.5 shadow-sm"
          role="group"
          aria-label="Période d'analyse"
          data-testid="admin-overview-period"
        >
          {PERIOD_OPTIONS.map(({ days: d, label }) => {
            const active = overviewDays === d;
            return (
              <Button
                key={d}
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  "h-8 min-w-0 rounded-md px-2.5 text-xs font-semibold tabular-nums sm:px-3",
                  active
                    ? "bg-[#0f172a] text-white shadow-sm hover:bg-[#0f172a] hover:text-white"
                    : "text-slate-600 hover:bg-white hover:text-slate-900",
                )}
                onClick={() => onOverviewDaysChange(d)}
                disabled={loading}
                data-testid={`admin-period-${d}`}
              >
                {label}
              </Button>
            );
          })}
        </div>
      </div>

      {urgencies.length > 0 ? (
        <section className="space-y-2" data-testid="admin-dash-urgencies">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">À traiter maintenant</h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {urgencies.map((u) => (
              <UrgencyCard key={u.key} {...u} />
            ))}
          </div>
        </section>
      ) : null}

      {loading && !stats ? (
        <p className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          Chargement des indicateurs…
        </p>
      ) : null}

      {stats ? (
        <section
          className="rounded-xl border border-[#2D7FF9]/20 bg-gradient-to-br from-sky-50/80 via-white to-white p-4 shadow-sm sm:p-5"
          data-testid="admin-dash-saas"
        >
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="font-display text-sm font-bold text-[#0B0D12] sm:text-base">
                Abonnements acheteurs
              </h3>
              <p className="mt-0.5 text-xs text-slate-600">
                Évolution, acquisition et churn sur {periodLabel} — MRR / ARR en instantané
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="tabular-nums text-[10px] font-semibold">
                {nFmt(activeSubs)} actifs
              </Badge>
              {Number(stats.subscriptions_pricing_gaps_active || 0) > 0 ? (
                <Badge
                  variant="outline"
                  className="max-w-[min(100%,18rem)] border-amber-300 text-amber-900 bg-amber-50 text-left whitespace-normal text-[10px]"
                >
                  {stats.subscriptions_pricing_gaps_active} sans prix Stripe
                </Badge>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 mb-4">
            <ClickableKpiCard
              label="Abonnés actifs"
              value={nFmt(activeSubs)}
              subValue={subsStart > 0 ? `Début période : ${nFmt(subsStart)}` : null}
              hint="État Stripe actuel"
              Icon={Users}
              onClick={() => onNavigateTab?.("users")}
              testId="admin-saas-active"
            />
            <ClickableKpiCard
              label="Nouveaux abonnés"
              value={nFmt(subsNew)}
              hint={`Activations · ${periodLabel}`}
              Icon={UserPlus}
              onClick={() => onNavigateTab?.("users")}
              testId="admin-saas-new"
              variant="positive"
            />
            <ClickableKpiCard
              label="Résiliations"
              value={nFmt(subsChurned)}
              hint={`Churn · ${periodLabel}`}
              Icon={UserMinus}
              onClick={() => onNavigateTab?.("users", { usersFilter: "churned" })}
              testId="admin-saas-churned"
              variant="negative"
            />
            <ClickableKpiCard
              label="Taux de churn"
              value={pctFmt(churnRate)}
              hint={subsStart > 0 ? `Sur ${nFmt(subsStart)} au début de période` : "Base début période nulle"}
              Icon={TrendingDown}
              testId="admin-saas-churn-rate"
              variant={churnRate > 5 ? "negative" : "default"}
            />
            <ClickableKpiCard
              label="Croissance nette"
              value={subsNet >= 0 ? `+${nFmt(subsNet)}` : nFmt(subsNet)}
              hint="Nouveaux − résiliations"
              Icon={subsNet >= 0 ? TrendingUp : TrendingDown}
              testId="admin-saas-net"
              variant={subsNet >= 0 ? "positive" : "negative"}
            />
            <ClickableKpiCard
              label="MRR"
              value={eurFmt(mrr)}
              subValue={`ARR ${eurFmt(stats.arr_eur)} · ARPU ${activeSubs > 0 ? eurFmt(arpu, 2) : "—"}`}
              hint="Revenu récurrent mensuel"
              Icon={LineChart}
              testId="admin-saas-mrr"
            />
          </div>

          <div className="rounded-lg border border-slate-200/80 bg-white p-3 sm:p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold text-slate-700">Évolution des abonnés actifs</p>
              <div className="flex flex-wrap items-center gap-3 text-[10px] font-semibold">
                <span className="inline-flex items-center gap-1 text-[#2D7FF9]">
                  <span className="h-0.5 w-4 bg-[#2D7FF9]" aria-hidden />
                  Actifs
                </span>
                <span className="inline-flex items-center gap-1 text-emerald-600">
                  <span className="h-2 w-2 rounded-sm bg-emerald-500/70" aria-hidden />
                  Nouveaux
                </span>
                <span className="inline-flex items-center gap-1 text-red-500">
                  <span className="h-2 w-2 rounded-sm bg-red-400/80" aria-hidden />
                  Résiliations
                </span>
              </div>
            </div>
            <div className="h-48 w-full min-w-0 sm:h-52" data-testid="admin-saas-chart">
              {subsChartEmpty ? (
                <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/50 px-4 text-center text-sm text-slate-500">
                  Aucune donnée abonnement sur cette période.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={subsChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={false}
                      tickLine={false}
                      interval={chartXAxisInterval(days)}
                      minTickGap={days > 90 ? 28 : 8}
                    />
                    <YAxis
                      yAxisId="left"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={false}
                      tickLine={false}
                      width={36}
                      allowDecimals={false}
                      domain={[0, Math.max(subsChartMax * 1.15, 2)]}
                    />
                    <YAxis yAxisId="right" orientation="right" hide />
                    <Tooltip
                      contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }}
                      formatter={(value, name) => {
                        const labels = {
                          active: "Abonnés actifs",
                          newSubs: "Nouveaux",
                          churned: "Résiliations",
                        };
                        return [nFmt(value), labels[name] || name];
                      }}
                      labelFormatter={(_, payload) => {
                        const raw = payload?.[0]?.payload?.date;
                        if (!raw) return "";
                        return new Date(`${raw}T12:00:00`).toLocaleDateString("fr-FR", {
                          weekday: "short",
                          day: "numeric",
                          month: "long",
                        });
                      }}
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="newSubs"
                      fill="#10b981"
                      fillOpacity={0.55}
                      radius={[2, 2, 0, 0]}
                      maxBarSize={days > 90 ? 6 : 14}
                      isAnimationActive={days <= 90}
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="churned"
                      fill="#f87171"
                      fillOpacity={0.65}
                      radius={[2, 2, 0, 0]}
                      maxBarSize={days > 90 ? 6 : 14}
                      isAnimationActive={days <= 90}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="active"
                      stroke="#2D7FF9"
                      strokeWidth={2.5}
                      dot={days <= 31}
                      isAnimationActive={days <= 90}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
            {subPeriod && !subPeriod.has_lifecycle_dates ? (
              <p className="mt-2 text-[11px] text-amber-800">
                Dates d’activation / résiliation estimées — appliquez la migration 069 pour un suivi précis à partir des
                webhooks Stripe.
              </p>
            ) : null}
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:hidden">
              <SaasMiniKpi label="Mensuel" value={nFmt(stats.subscribers_monthly_count)} Icon={Users} testId="admin-saas-m" />
              <SaasMiniKpi label="Annuel" value={nFmt(stats.subscribers_yearly_count)} Icon={Users} testId="admin-saas-y" />
              <SaasMiniKpi
                label="Inactifs"
                value={nFmt(stats.subscriptions_inactive_count)}
                Icon={UserMinus}
                tone="red"
                testId="admin-saas-inactive"
              />
            </div>
          </div>
        </section>
      ) : null}

      {loading && !period ? (
        <p className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Chargement marketplace…
        </p>
      ) : null}

      {marketplaceKpis ? (
        <section>
          <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">
            Marketplace · {periodLabel}
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
            {marketplaceKpis.map((k) => (
              <ClickableKpiCard key={k.key} {...k} />
            ))}
          </div>
        </section>
      ) : !loading && stats ? (
        <p className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-900">
          Indicateurs marketplace indisponibles. Vérifiez les droits admin.
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <section className="lg:col-span-3 rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm sm:p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="font-display text-sm font-bold text-[#0B0D12]">Commissions &amp; GMV</h3>
              <p className="text-xs text-slate-500">Courbes journalières · {periodLabel}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-[10px] font-semibold">
              <span className="inline-flex items-center gap-1 text-[#2D7FF9]">
                <span className="h-2 w-4 rounded-sm bg-[#2D7FF9]/60" aria-hidden />
                Commissions
              </span>
              <span className="inline-flex items-center gap-1 text-emerald-600">
                <span className="h-0.5 w-4 bg-emerald-500" aria-hidden />
                GMV
              </span>
            </div>
          </div>
          <div className="h-52 w-full min-w-0 sm:h-56" data-testid="admin-dash-chart">
            {gmvChartEmpty ? (
              <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/50 px-4 text-center text-sm text-slate-500">
                Aucune donnée sur cette période.
              </div>
            ) : gmvChartNoRevenue ? (
              <div className="flex h-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-200 bg-slate-50/50 px-4 text-center">
                <p className="text-sm font-medium text-slate-700">Aucune vente confirmée sur {periodLabel}</p>
                <button
                  type="button"
                  className="text-xs font-semibold text-[#2D7FF9] hover:underline"
                  onClick={() => onOpenTransactions?.({ filter: "all" })}
                >
                  Voir toutes les transactions →
                </button>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={gmvChartData} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
                  <defs>
                    <linearGradient id="adminCommissionFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2D7FF9" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#2D7FF9" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                    interval={chartXAxisInterval(days)}
                    minTickGap={days > 90 ? 28 : 8}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    axisLine={false}
                    tickLine={false}
                    width={56}
                    tickFormatter={(v) => `${v} €`}
                    domain={[0, gmvChartMax * 1.12]}
                    allowDecimals={gmvChartMax < 10}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }}
                    formatter={(value, name) => {
                      const label = name === "gmv" ? "GMV" : "Commissions";
                      return [eurFmt(value), label];
                    }}
                    labelFormatter={(_, payload) => {
                      const raw = payload?.[0]?.payload?.date;
                      if (!raw) return "";
                      return new Date(`${raw}T12:00:00`).toLocaleDateString("fr-FR", {
                        weekday: "short",
                        day: "numeric",
                        month: "long",
                      });
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="commissions"
                    stroke="#2D7FF9"
                    strokeWidth={2}
                    fill="url(#adminCommissionFill)"
                    dot={days <= 7}
                    isAnimationActive={days <= 90}
                  />
                  <Line
                    type="monotone"
                    dataKey="gmv"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={days <= 90}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
          {period ? (
            <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
              <button
                type="button"
                className="text-xs font-semibold text-[#2D7FF9] hover:underline"
                onClick={() => onOpenTransactions?.({ filter: "all" })}
              >
                Toutes les transactions →
              </button>
              {(snapshot.txAttention ?? 0) > 0 ? (
                <button
                  type="button"
                  className="text-xs font-semibold text-amber-800 hover:underline"
                  onClick={() => onOpenTransactions?.({ filter: "attention" })}
                >
                  {snapshot.txAttention} à surveiller →
                </button>
              ) : null}
            </div>
          ) : null}
        </section>

        <section className="lg:col-span-2 rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm sm:p-5">
          <h3 className="font-display text-sm font-bold text-[#0B0D12]">Activité récente</h3>
          <p className="mt-0.5 text-xs text-slate-500">Cliquez un événement pour ouvrir le détail</p>
          <ul className="mt-4 space-y-1 max-h-[min(280px,50vh)] overflow-y-auto" data-testid="admin-dash-activity">
            {activity.length === 0 ? (
              <li className="py-6 text-center text-sm text-slate-500">Aucune activité sur la période.</li>
            ) : (
              activity.map((a, i) => {
                const Icon = ACTIVITY_ICON[a.type] || Sparkles;
                const tone = ACTIVITY_TONE[a.type] || "bg-slate-100 text-slate-700";
                const clickable = Boolean(a.entity_id);
                const Tag = clickable ? "button" : "li";
                return (
                  <li key={`${a.at}-${a.type}-${i}`} className="list-none">
                    <Tag
                      type={clickable ? "button" : undefined}
                      onClick={clickable ? () => handleActivityClick(a) : undefined}
                      className={cn(
                        "flex w-full gap-3 rounded-lg px-1 py-2 text-left transition-colors",
                        clickable && "hover:bg-slate-50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7FF9]/30",
                      )}
                    >
                      <div
                        className={cn(
                          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
                          tone,
                        )}
                      >
                        <Icon className="h-4 w-4" aria-hidden />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium leading-snug text-slate-800 line-clamp-2">{a.label}</p>
                        <p className="mt-0.5 text-[11px] text-slate-500 tabular-nums">
                          {formatActivityWhen(a.at)}
                          {clickable ? " · Ouvrir" : ""}
                        </p>
                      </div>
                      {clickable ? (
                        <ArrowRight className="h-4 w-4 shrink-0 self-center text-slate-300" aria-hidden />
                      ) : null}
                    </Tag>
                  </li>
                );
              })
            )}
          </ul>
        </section>
      </div>

      {stats ? (
        <section>
          <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">Stock plateforme</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <SnapshotTile
              label="Annonces en ligne"
              value={nFmt(stats.dossiers_online)}
              onClick={() => onNavigateTab?.("pending")}
              testId="admin-snap-online"
            />
            <SnapshotTile
              label="Ventes (total)"
              value={nFmt(stats.dossiers_sold)}
              hint="RDV confirmés"
              onClick={() => onOpenTransactions?.({ filter: "all" })}
              testId="admin-snap-sold"
            />
            <SnapshotTile
              label="Utilisateurs"
              value={nFmt(stats.users_count)}
              onClick={() => onNavigateTab?.("users")}
              testId="admin-snap-users"
            />
            <SnapshotTile
              label="Abonnés actifs"
              value={nFmt(activeSubs)}
              hint="Instantané"
              onClick={() => onNavigateTab?.("users")}
              testId="admin-snap-subs"
            />
            <SnapshotTile
              label="Nouvelles annonces"
              value={nFmt(period?.new_listings_online ?? 0)}
              hint={periodLabel}
              onClick={() => onNavigateTab?.("pending")}
              testId="admin-snap-listings"
            />
            <SnapshotTile
              label="Litiges ouverts"
              value={nFmt(snapshot.disputesOpen ?? stats.disputes_open ?? 0)}
              onClick={() => onNavigateTab?.("disputes")}
              testId="admin-snap-disputes"
            />
          </div>
        </section>
      ) : null}
    </div>
  );
}
